import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { selectPendingPayment } from "../../redux/features/restaurants/restaurantSelectors";
import { clearPendingPayment } from "../../redux/features/restaurants/restaurantSlice";
import { getCateringOrderStatus } from "../../api/get/getCateringOrderStatus";

/**
 * Reusable Pending Payment Warning Modal
 * 
 * This modal checks if there's a pending payment using the getCateringOrderStatus API
 * and shows a warning before proceeding with an action that would clear the cart.
 * 
 * Usage:
 * 1. Call checkAndProceed() before any action that might clear the cart
 * 2. If there's a pending payment, the modal will show
 * 3. If user confirms, onProceed callback is called
 * 4. If user cancels or no pending payment, no modal is shown
 */
const PendingPaymentWarningModal = ({ 
  isOpen,
  onClose,
  onProceed,
  actionDescription = "this action",
  additionalContext = ""
}) => {
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const dispatch = useDispatch();
  const pendingPayment = useSelector(selectPendingPayment);

  // Get the order number from pending payment
  const orderNo = pendingPayment?.orderInfo?.orderNo || pendingPayment?.orderNo;

  const handleConfirm = () => {
    // Clear pending payment state
    dispatch(clearPendingPayment());
    
    // Call the proceed callback
    if (onProceed) {
      onProceed();
    }
    
    // Close modal
    if (onClose) {
      onClose();
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex items-center mb-4">
          <svg className="w-6 h-6 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <h3 className="text-lg font-bold text-gray-800">
            Payment Pending
          </h3>
        </div>
        <div className="text-gray-600 mb-6">
          <p className="mb-2">
            A customer is currently completing payment for an existing order{orderNo && ` (Order #${orderNo})`}. 
          </p>
          <p className="mb-2">
            If you proceed with {actionDescription}, the current pending payment will be cancelled and the cart will be reset.
          </p>
          {additionalContext && (
            <p className="font-semibold text-gray-700">
              {additionalContext}
            </p>
          )}
          <p className="mt-3 font-semibold text-amber-700">
            Are you sure you want to proceed?
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleCancel}
            disabled={isCheckingStatus}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isCheckingStatus}
            className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
          >
            {isCheckingStatus ? "Checking..." : "Yes, Proceed Anyway"}
          </button>
        </div>
      </div>
    </div>
  );
};

PendingPaymentWarningModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
  actionDescription: PropTypes.string,
  additionalContext: PropTypes.string,
};

/**
 * Hook to check for pending payment before proceeding with an action
 * 
 * @returns {Object} - { checkAndProceed, showWarning, setShowWarning }
 * 
 * Usage:
 * const { checkAndProceed, showWarning, setShowWarning } = usePendingPaymentCheck();
 * 
 * // In your action handler:
 * const handleAction = async () => {
 *   const canProceed = await checkAndProceed();
 *   if (!canProceed) return; // Will show modal if needed
 *   
 *   // Your action logic here
 * };
 */
export const usePendingPaymentCheck = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const pendingPayment = useSelector(selectPendingPayment);

  /**
   * Check if there's a pending payment by calling the order status API
   * @param {Function} onProceed - Callback to execute if check passes or user confirms
   * @returns {Promise<boolean>} - true if can proceed immediately, false if modal shown
   */
  const checkAndProceed = async (onProceed) => {
    // First check Redux state for pending payment
    if (!pendingPayment || pendingPayment.status !== "Pending") {
      console.log("No pending payment in Redux, proceeding normally");
      return true; // No pending payment, can proceed
    }

    console.log("Pending payment detected in Redux:", pendingPayment);

    // Get order number from pending payment
    const orderNo = pendingPayment?.orderInfo?.orderNo || pendingPayment?.orderNo;

    if (!orderNo) {
      console.warn("No order number found in pending payment, proceeding normally");
      return true; // Can't verify without order number, allow to proceed
    }

    try {
      console.log("Checking order status for:", orderNo);
      
      // Call the order status API to verify if payment is actually still pending
      const statusResponse = await getCateringOrderStatus(orderNo);
      console.log("Order status response:", statusResponse);

      // Check if the order status indicates payment is still pending
      const orderStatus = statusResponse?.data?.order_status || statusResponse?.order_status;
      const isPendingPayment = orderStatus === "Pending Payment" || orderStatus === "Pending";

      if (!isPendingPayment) {
        console.log("Payment is no longer pending (status:", orderStatus, "), proceeding normally");
        return true; // Payment is no longer pending, can proceed
      }

      console.log("Payment is still pending, showing warning modal");
      
      // Payment is still pending, show warning modal
      setPendingAction(() => onProceed);
      setShowWarning(true);
      return false; // Modal will be shown, don't proceed yet
    } catch (error) {
      console.error("Error checking order status:", error);
      // If API call fails, still show the warning to be safe
      console.log("API error, showing warning modal to be safe");
      setPendingAction(() => onProceed);
      setShowWarning(true);
      return false;
    }
  };

  const handleProceed = () => {
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
    setShowWarning(false);
  };

  const handleCancel = () => {
    setPendingAction(null);
    setShowWarning(false);
  };

  return {
    checkAndProceed,
    showWarning,
    setShowWarning,
    handleProceed,
    handleCancel,
  };
};

export default PendingPaymentWarningModal;

